import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  MenuItem,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Collapse
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  X, 
  ChevronDown, 
  ChevronUp, 
  Phone, 
  Mail, 
  Clock,
  MapPin
} from 'lucide-react';

const Navbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const location = useLocation();
  
  const [drawer, setDrawer] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [portfolioOpen, setPortfolioOpen] = useState(false);
  const [anchorElServices, setAnchorElServices] = useState(null);
  const [anchorElPortfolio, setAnchorElPortfolio] = useState(null);
  
  const handleOpenServicesMenu = (event) => {
    if (!isMobile) {
      setAnchorElServices(event.currentTarget);
    } else {
      setServicesOpen(!servicesOpen);
    }
  };
  
  const handleOpenPortfolioMenu = (event) => {
    if (!isMobile) {
      setAnchorElPortfolio(event.currentTarget);
    } else {
      setPortfolioOpen(!portfolioOpen);
    }
  };
  
  const handleCloseServicesMenu = () => {
    setAnchorElServices(null);
  };
  
  const handleClosePortfolioMenu = () => {
    setAnchorElPortfolio(null);
  };

  // Track scroll position for navbar effects  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setDrawer(!drawer);
  };
  
  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { 
      name: 'Services', 
      path: '/services',
      hasSubmenu: true,
      handleClick: handleOpenServicesMenu,
      submenuItems: [
        { name: 'Interior Design', path: '/services/interior-design' },
        { name: '3D Visualization', path: '/services/3d-visualization' },
        { name: 'Commercial Design', path: '/services/commercial-design' },
        { name: 'Residential Design', path: '/services/residential-design' },
        { name: 'Space Planning', path: '/services/space-planning' }
      ]
    },
    { 
      name: 'Portfolio', 
      path: '/portfolio',
      hasSubmenu: true,
      handleClick: handleOpenPortfolioMenu,
      submenuItems: [
        { name: 'Residential', path: '/portfolio/residential' },
        { name: 'Commercial', path: '/portfolio/commercial' },
        { name: 'Hospitality', path: '/portfolio/hospitality' },
        { name: 'Institutional', path: '/portfolio/institutional' },
      ]
    },
    { name: 'Contact', path: '/contact' }
  ];

  // Helper to check if path is active
  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Updated Logo Component with consistent styling
  const Logo = () => (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box
        sx={{
          width: 42,
          height: 42,
          position: 'relative',
          mr: 1.5,
          borderRadius: '10px',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 10px rgba(255, 87, 34, 0.3)',
        }}
      >
        {/* Floor Plan Design */}
        <Box sx={{
          position: 'relative',
          width: '75%',
          height: '75%',
          zIndex: 2,
        }}>
          {/* Outer Wall */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: '2px solid white',
          }} />
          
          {/* Inner Walls */}
          <Box sx={{
            position: 'absolute',
            top: '30%',
            left: 0,
            width: '65%',
            height: '2px',
            backgroundColor: 'white',
          }} />
          
          <Box sx={{
            position: 'absolute',
            top: '30%',
            right: 0,
            width: '20%',
            height: '2px',
            backgroundColor: 'white',
          }} />
          
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '65%',
            width: '35%',
            height: '2px',
            backgroundColor: 'white',
          }} />
          
          <Box sx={{
            position: 'absolute',
            top: '65%',
            left: 0,
            width: '60%',
            height: '2px',
            backgroundColor: 'white',
          }} />
          
          <Box sx={{
            position: 'absolute',
            top: '30%',
            left: '65%',
            width: '2px',
            height: '70%',
            backgroundColor: 'white',
          }} />
        </Box>
      </Box>
      <Box>
        <Typography
          variant="h6"
          noWrap
          component={RouterLink}
          to="/"
          sx={{
            fontWeight: 700,
            letterSpacing: '.05rem',
            color: 'white',
            textDecoration: 'none',
            fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
            fontSize: { xs: '1.2rem', md: '1.4rem' }
          }}
        >
          Vecto<Box component="span" sx={{ color: '#FF5722' }}>Space</Box>
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'rgba(255,255,255,0.7)',
            letterSpacing: '0.1em',
            fontSize: '0.65rem',
            lineHeight: 1,
            textTransform: 'uppercase',
            fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
          }}
        >
          Interior Design Studio
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Announcement Bar */}
      <Box
        sx={{
          background: 'linear-gradient(to right, #111111, #212121)',
          color: 'rgba(255,255,255,0.8)',
          py: 1,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          display: { xs: 'none', md: 'block' }
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mr: 3,
                  transition: 'color 0.3s ease',
                  '&:hover': { color: '#FF5722' }
                }}
              >
                <Phone size={14} style={{ marginRight: 6 }} />
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.75rem',
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  +91 40 6678 9000
                </Typography>
              </Box>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  transition: 'color 0.3s ease',
                  '&:hover': { color: '#FF5722' }
                }}
              >
                <Mail size={14} style={{ marginRight: 6 }} />
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.75rem',
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  info@vectospacedesign.com
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mr: 3,
                  transition: 'color 0.3s ease',
                  '&:hover': { color: '#FF5722' }
                }}
              >
                <Clock size={14} style={{ marginRight: 6 }} />
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.75rem',
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  Mon - Fri: 9:00 AM - 6:00 PM
                </Typography>
              </Box>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  transition: 'color 0.3s ease',
                  '&:hover': { color: '#FF5722' }
                }}
              >
                <MapPin size={14} style={{ marginRight: 6 }} />
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.75rem',
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  Hyderabad, India
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Main Navbar */}
      <AppBar 
        position="sticky" 
        elevation={scrolled ? 4 : 0}
        sx={{
          bgcolor: scrolled ? 'rgba(0, 0, 0, 0.95)' : 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(10px)',
          borderBottom: scrolled ? 'none' : '1px solid rgba(255,255,255,0.1)',
          transition: 'all 0.3s ease',
          boxShadow: scrolled ? '0 4px 20px rgba(0,0,0,0.3)' : 'none',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ py: { xs: 1, md: scrolled ? 1 : 1.5 } }}>
            {/* Logo */}
            <Logo />

            {/* Mobile menu button */}
            {isMobile && (
              <IconButton
                size="large"
                aria-label="menu"
                onClick={handleDrawerToggle}
                sx={{ 
                  ml: 'auto', 
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255,87,34,0.2)'
                  }
                }}
              >
                {drawer ? <X /> : <MenuIcon />}
              </IconButton>
            )}

            {/* Desktop Navigation */}
            {!isMobile && (
              <>
                <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                  {navItems.map((item) => (
                    <React.Fragment key={item.name}>
                      {item.hasSubmenu ? (
                        <Box sx={{ position: 'relative' }}>
                          <Button
                            onClick={item.handleClick}
                            sx={{
                              mx: { md: 1, lg: 2 },
                              color: 'white',
                              fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                              fontWeight: isActive(item.path) ? 600 : 500,
                              letterSpacing: 0.5,
                              '&:hover': {
                                color: '#FF5722',
                                backgroundColor: 'rgba(255,87,34,0.1)'
                              },
                              '&::after': isActive(item.path) ? {
                                content: '""',
                                position: 'absolute',
                                bottom: 6,
                                left: '30%',
                                width: '40%',
                                height: 3,
                                background: 'linear-gradient(to right, #FF5722, #FF8A65)',
                                borderRadius: 1
                              } : {}
                            }}
                            endIcon={
                              (item.name === 'Services' && Boolean(anchorElServices)) || 
                              (item.name === 'Portfolio' && Boolean(anchorElPortfolio)) ? 
                                <ChevronUp size={16} /> : <ChevronDown size={16} />
                            }
                          >
                            {item.name}
                          </Button>
                          
                          {/* Services Dropdown */}
                          {item.name === 'Services' && (
                            <Menu
                              anchorEl={anchorElServices}
                              open={Boolean(anchorElServices)}
                              onClose={handleCloseServicesMenu}
                              sx={{
                                '& .MuiPaper-root': {
                                  mt: 1.5,
                                  backgroundImage: 'linear-gradient(to bottom, #212121, #000000)',
                                  borderRadius: 0,
                                  border: '1px solid rgba(255,255,255,0.1)',
                                  boxShadow: '0 8px 20px rgba(0,0,0,0.4)',
                                  overflow: 'hidden',
                                  minWidth: 200
                                }
                              }}
                            >
                              {item.submenuItems.map((subItem) => (
                                <MenuItem 
                                  key={subItem.name} 
                                  component={RouterLink}
                                  to={subItem.path}
                                  onClick={handleCloseServicesMenu}
                                  sx={{
                                    color: 'white',
                                    py: 1.5,
                                    '&:hover': {
                                      backgroundColor: 'rgba(255,87,34,0.1)',
                                      color: '#FF5722'
                                    }
                                  }}
                                >
                                  <Typography 
                                    variant="body2"
                                    sx={{
                                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                                    }}
                                  >
                                    {subItem.name}
                                  </Typography>
                                </MenuItem>
                              ))}
                            </Menu>
                          )}
                          
                          {/* Portfolio Dropdown */}
                          {item.name === 'Portfolio' && (
                            <Menu
                              anchorEl={anchorElPortfolio}
                              open={Boolean(anchorElPortfolio)}
                              onClose={handleClosePortfolioMenu}
                              sx={{
                                '& .MuiPaper-root': {
                                  mt: 1.5,
                                  backgroundImage: 'linear-gradient(to bottom, #212121, #000000)',
                                  borderRadius: 0,
                                  border: '1px solid rgba(255,255,255,0.1)',
                                  boxShadow: '0 8px 20px rgba(0,0,0,0.4)',
                                  overflow: 'hidden',
                                  minWidth: 180
                                }
                              }}
                            >
                              {item.submenuItems.map((subItem) => (
                                <MenuItem 
                                  key={subItem.name} 
                                  component={RouterLink}
                                  to={subItem.path}
                                  onClick={handleClosePortfolioMenu}
                                  sx={{
                                    color: 'white',
                                    py: 1.5,
                                    '&:hover': {
                                      backgroundColor: 'rgba(255,87,34,0.1)',
                                      color: '#FF5722'
                                    }
                                  }}
                                >
                                  <Typography 
                                    variant="body2"
                                    sx={{
                                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                                    }}
                                  >
                                    {subItem.name}
                                  </Typography>
                                </MenuItem>
                              ))}
                            </Menu>
                          )}
                        </Box>
                      ) : (
                        <Button
                          component={RouterLink}
                          to={item.path}
                          sx={{
                            mx: { md: 1, lg: 2 },
                            color: 'white',
                            fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                            fontWeight: isActive(item.path) ? 600 : 500,
                            letterSpacing: 0.5,
                            position: 'relative',
                            '&:hover': {
                              color: '#FF5722',
                              backgroundColor: 'rgba(255,87,34,0.1)'
                            },
                            '&::after': isActive(item.path) ? {
                              content: '""',
                              position: 'absolute',
                              bottom: 6,
                              left: '30%',
                              width: '40%',
                              height: 3,
                              background: 'linear-gradient(to right, #FF5722, #FF8A65)',
                              borderRadius: 1
                            } : {}
                          }}
                        >
                          {item.name}
                        </Button>
                      )}
                    </React.Fragment>
                  ))}
                </Box>

                <Button
                  component={RouterLink}
                  to="/contact"
                  variant="contained"
                  sx={{ 
                    borderRadius: 0, 
                    px: 3,
                    py: 1,
                    fontWeight: 600,
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                    bgcolor: '#FF5722',
                    color: 'white',
                    boxShadow: '0 4px 15px rgba(255, 87, 34, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      bgcolor: '#E64A19',
                      boxShadow: '0 6px 20px rgba(255, 87, 34, 0.4)',
                    }
                  }}
                >
                  Get a Quote
                </Button>
              </>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawer}
        onClose={handleDrawerToggle}
        sx={{
          '& .MuiDrawer-paper': {
            width: '85%',
            maxWidth: 350,
            backgroundColor: '#121212',
            backgroundImage: 'linear-gradient(to bottom right, #212121, #000000)',
            borderLeft: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '-5px 0px 25px rgba(0,0,0,0.5)',
          }
        }}
      >
        <Box sx={{ p: 3, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Logo />
            <IconButton
              onClick={handleDrawerToggle}
              sx={{ 
                color: 'rgba(255,255,255,0.7)',
                '&:hover': {
                  backgroundColor: 'rgba(255,87,34,0.1)',
                  color: '#FF5722'
                }
              }}
            >
              <X size={24} />
            </IconButton>
          </Box>
        </Box>

        {/* Mobile Announcement Info */}
        <Box sx={{ p: 3, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Phone size={16} style={{ color: '#FF5722', marginRight: 12 }} />
            <Typography 
              variant="body2" 
              color="rgba(255,255,255,0.8)"
              sx={{ fontFamily: '"Inter", "Helvetica", "Arial", sans-serif' }}
            >
              +91 40 6678 9000
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Mail size={16} style={{ color: '#FF5722', marginRight: 12 }} />
            <Typography 
              variant="body2" 
              color="rgba(255,255,255,0.8)"
              sx={{ fontFamily: '"Inter", "Helvetica", "Arial", sans-serif' }}
            >
              info@vectospacedesign.com
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Clock size={16} style={{ color: '#FF5722', marginRight: 12 }} />
            <Typography 
              variant="body2" 
              color="rgba(255,255,255,0.8)"
              sx={{ fontFamily: '"Inter", "Helvetica", "Arial", sans-serif' }}
            >
              Mon - Fri: 9:00 AM - 6:00 PM
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <MapPin size={16} style={{ color: '#FF5722', marginRight: 12 }} />
            <Typography 
              variant="body2" 
              color="rgba(255,255,255,0.8)"
              sx={{ fontFamily: '"Inter", "Helvetica", "Arial", sans-serif' }}
            >
              Hyderabad, India
            </Typography>
          </Box>
        </Box>

        {/* Mobile Navigation */}
        <List sx={{ py: 0 }}>
          {navItems.map((item) => (
            <React.Fragment key={item.name}>
              {item.hasSubmenu ? (
                <>
                  <ListItem 
                    disableGutters 
                    disablePadding
                    sx={{
                      borderBottom: '1px solid rgba(255,255,255,0.05)',
                      bgcolor: isActive(item.path) ? 'rgba(255,87,34,0.1)' : 'transparent'
                    }}
                  >
                    <ListItemButton
                      onClick={item.handleClick}
                      sx={{
                        py: 2,
                        px: 3,
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(255,87,34,0.1)'
                        }
                      }}
                    >
                      <ListItemText 
                        primary={item.name} 
                        primaryTypographyProps={{
                          fontWeight: isActive(item.path) ? 600 : 400,
                          fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                        }}
                      />
                      {item.name === 'Services' && (servicesOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />)}
                      {item.name === 'Portfolio' && (portfolioOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />)}
                    </ListItemButton>
                  </ListItem>
                  
                  {/* Services Submenu */}
                  {item.name === 'Services' && (
                    <Collapse in={servicesOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding sx={{ bgcolor: 'rgba(0,0,0,0.3)' }}>
                        {item.submenuItems.map((subItem) => (
                          <ListItem 
                            key={subItem.name} 
                            disableGutters 
                            disablePadding
                            sx={{
                              borderBottom: '1px solid rgba(255,255,255,0.05)'
                            }}
                          >
                            <ListItemButton
                              component={RouterLink}
                              to={subItem.path}
                              onClick={handleDrawerToggle}
                              sx={{
                                py: 1.5,
                                pl: 6,
                                pr: 3,
                                color: 'rgba(255,255,255,0.7)',
                                '&:hover': {
                                  color: 'white',
                                  backgroundColor: 'rgba(255,87,34,0.1)'
                                }
                              }}
                            >
                              <ListItemText 
                                primary={subItem.name} 
                                primaryTypographyProps={{
                                  fontSize: '0.9rem',
                                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  )}
                  
                  {/* Portfolio Submenu */}
                  {item.name === 'Portfolio' && (
                    <Collapse in={portfolioOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding sx={{ bgcolor: 'rgba(0,0,0,0.3)' }}>
                        {item.submenuItems.map((subItem) => (
                          <ListItem 
                            key={subItem.name} 
                            disableGutters 
                            disablePadding
                            sx={{
                              borderBottom: '1px solid rgba(255,255,255,0.05)'
                            }}
                          >
                            <ListItemButton
                              component={RouterLink}
                              to={subItem.path}
                              onClick={handleDrawerToggle}
                              sx={{
                                py: 1.5,
                                pl: 6,
                                pr: 3,
                                color: 'rgba(255,255,255,0.7)',
                                '&:hover': {
                                  color: 'white',
                                  backgroundColor: 'rgba(255,87,34,0.1)'
                                }
                              }}
                            >
                              <ListItemText 
                                primary={subItem.name} 
                                primaryTypographyProps={{
                                  fontSize: '0.9rem',
                                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  )}
                </>
              ) : (
                <ListItem 
                  disableGutters 
                  disablePadding
                  sx={{
                    borderBottom: '1px solid rgba(255,255,255,0.05)',
                    bgcolor: isActive(item.path) ? 'rgba(255,87,34,0.1)' : 'transparent'
                  }}
                >
                  <ListItemButton
                    component={RouterLink}
                    to={item.path}
                    onClick={handleDrawerToggle}
                    sx={{
                      py: 2,
                      px: 3,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(255,87,34,0.05)'
                      }
                    }}
                  >
                    <ListItemText 
                      primary={item.name} 
                      primaryTypographyProps={{
                        fontWeight: isActive(item.path) ? 600 : 400,
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )}
            </React.Fragment>
          ))}
        </List>

        {/* CTA Button in Drawer */}
        <Box sx={{ p: 3, mt: 2 }}>
          <Button
            component={RouterLink}
            to="/contact"
            variant="contained"
            fullWidth
            size="large"
            onClick={handleDrawerToggle}
            sx={{ 
              py: 1.5,
              borderRadius: 0, 
              fontWeight: 600,
              fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
              textTransform: 'uppercase',
              letterSpacing: 1,
              bgcolor: '#FF5722',
              color: 'white',
              '&:hover': {
                bgcolor: '#E64A19',
              }
            }}
          >
            Get a Quote
          </Button>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;